import React, { useState, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import * as XLSX from 'xlsx';

const COLUMN_MAPPING = {
  'account n': 'account_number', 'account #': 'account_number', 'account_number': 'account_number', 'account number': 'account_number',
  'ds trade': 'ds_trade', 'ds_trade': 'ds_trade',
  'ds work s': 'ds_work_schedule', 'ds work schedule': 'ds_work_schedule', 'ds_work_schedule': 'ds_work_schedule',
  'ds nb day': 'ds_nb_day', 'ds_nb_day': 'ds_nb_day',
  'ds straigh': 'ds_straight', 'ds straight': 'ds_straight', 'ds_straight': 'ds_straight',
  'ds over ti': 'ds_overtime', 'ds overtime': 'ds_overtime', 'ds_overtime': 'ds_overtime',
  'ds double': 'ds_double', 'ds_double': 'ds_double',
  'ds total w': 'ds_total_wages', 'ds total wages': 'ds_total_wages', 'ds_total_wages': 'ds_total_wages',
  'ds additio': 'ds_addition', 'ds addition': 'ds_addition', 'ds_addition': 'ds_addition',
  'ds blank': 'ds_blank', 'ds_blank': 'ds_blank',
  'ds notes': 'ds_notes', 'ds_notes': 'ds_notes', 'ds notes,': 'ds_notes',
  'column1': 'column1',
  'ns trade': 'ns_trade', 'ns_trade': 'ns_trade',
  'ns work s': 'ns_work_schedule', 'ns work schedule': 'ns_work_schedule', 'ns_work_schedule': 'ns_work_schedule',
  'ns nb day': 'ns_nb_day', 'ns_nb_day': 'ns_nb_day',
  'ns straigh': 'ns_straight', 'ns straight': 'ns_straight', 'ns_straight': 'ns_straight',
  'ns over ti': 'ns_overtime', 'ns overtime': 'ns_overtime', 'ns_overtime': 'ns_overtime',
  'ns double': 'ns_double', 'ns_double': 'ns_double',
  'ns total w': 'ns_total_wages', 'ns total wages': 'ns_total_wages', 'ns_total_wages': 'ns_total_wages',
  'ns additio': 'ns_addition', 'ns addition': 'ns_addition', 'ns_addition': 'ns_addition',
  'ns blank': 'ns_blank', 'ns_blank': 'ns_blank',
  'ns notes': 'ns_notes', 'ns_notes': 'ns_notes', 'ns notes,': 'ns_notes'
};

const LabourRatesUploadModal = ({ onClose, onUploadComplete }) => {
  const { instance, accounts } = useMsal();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setError(null);
    try {
      const data = await f.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
      
      const mapped = json.map(row => {
        const newRow = {};
        Object.keys(row).forEach(key => {
          const lowerKey = key.toLowerCase().trim();
          const dbKey = COLUMN_MAPPING[lowerKey];
          if (dbKey) newRow[dbKey] = row[key];
        });
        return newRow;
      });
      setPreview(mapped.slice(0, 5));
    } catch (err) {
      setError('Failed to parse file: ' + err.message);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
      
      const mapped = json.map(row => {
        const newRow = {};
        Object.keys(row).forEach(key => {
          const lowerKey = key.toLowerCase().trim();
          const dbKey = COLUMN_MAPPING[lowerKey];
          if (dbKey) newRow[dbKey] = row[key];
        });
        return newRow;
      });
      
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/cost-breakdown/labour-rates/upload', { data: mapped });
      
      if (response.data.success) {
        alert(`Successfully imported ${response.data.rowsProcessed} records`);
        onUploadComplete();
        onClose();
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: '#2a2a3a', padding: '30px', borderRadius: '8px', maxWidth: '700px', width: '90%', maxHeight: '80vh', overflow: 'auto' }}>
        <h3 style={{ color: '#fff', marginTop: 0 }}>Import Labour Rates</h3>
        <input type="file" ref={fileRef} accept=".xlsx,.xls,.csv" onChange={handleFileChange} style={{ marginBottom: '20px', color: '#fff' }} />
        {error && <p style={{ color: '#ff6b6b' }}>{error}</p>}
        {preview.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ color: '#fff' }}>Preview (first 5 rows):</h4>
            <div style={{ overflow: 'auto', maxHeight: '200px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr>{Object.keys(preview[0]).map(k => <th key={k} style={{ backgroundColor: '#343a40', color: '#fff', padding: '6px', borderBottom: '1px solid #4a4a5a' }}>{k}</th>)}</tr>
                </thead>
                <tbody>
                  {preview.map((row, i) => <tr key={i}>{Object.values(row).map((v, j) => <td key={j} style={{ padding: '6px', borderBottom: '1px solid #3a3a4a', color: '#ccc' }}>{String(v).substring(0, 30)}</td>)}</tr>)}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleUpload} disabled={!file || uploading} style={{ padding: '10px 20px', backgroundColor: '#17a2b8', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{uploading ? 'Importing...' : 'Import'}</button>
        </div>
      </div>
    </div>
  );
};

export default LabourRatesUploadModal;
